import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { Site } from '@/rtk-query.gql';
import { ISite } from '@/services/auth';
import { useSites } from '@/services/sites';

export type SiteSelectOption = { id: string; name: string };

type SiteFilter = Parameters<typeof useSites>[0]['filter'];

type SiteSelectProps = {
  /** URL search param that stores the selected site id (when `urlSync`). Default `sId`. */
  paramKey?: string;
  label?: string;
  placeholder?: string;
  /** Wrapper class. */
  className?: string;
  /** Class applied to the input control. */
  triggerClassName?: string;
  disabled?: boolean;
  /** Whether the selected site can be cleared. Defaults to true. */
  allowClear?: boolean;
  /**
   * Explicit site list (e.g. `getValidSites(me)`). When provided, no fetch is
   * made. When omitted, sites are fetched via `useSites({ filter })`.
   */
  validSites?: Array<Site | ISite>;
  /** Filter used to fetch sites when `validSites` is not provided. */
  filter?: SiteFilter;
  /** Extra options appended to the list (e.g. hardcoded sites). */
  extraOptions?: SiteSelectOption[];
  /**
   * When true (default) the selection is read from / written to the URL param.
   * Set false to use the component as a controlled input via `value`/`onChange`.
   */
  urlSync?: boolean;
  /** Controlled selected id (used when `urlSync` is false). */
  value?: string | null;
  /** Fired with the new site id (or null when cleared). */
  onChange?: (id: string | null) => void;
};

/**
 * Base UI site autocomplete — the single source of truth for the "select a
 * site" control across modules. Type to filter; pass `validSites` for
 * permission-scoped lists or a `filter` to fetch them. Defaults to URL-param
 * sync, or set `urlSync={false}` to drive it from `value`/`onChange`.
 */
export const SiteSelect = ({
  paramKey = 'sId',
  label,
  placeholder = 'Select site',
  className,
  triggerClassName,
  disabled,
  allowClear = true,
  validSites,
  filter = 'aspiration',
  extraOptions,
  urlSync = true,
  value,
  onChange,
}: SiteSelectProps) => {
  const [params, setParams] = useSearchParams();
  const selected = urlSync ? params.get(paramKey) : (value ?? null);

  const { data: fetched, isLoading } = useSites({
    filter,
    enabled: !validSites,
  });

  const options = useMemo<SiteSelectOption[]>(() => {
    const base: SiteSelectOption[] = validSites
      ? validSites.map((s) => ({
          id: ('code' in s ? s.code : s.id) as string,
          name: s.name as string,
        }))
      : (fetched ?? []).map((s) => ({
          id: s.id as string,
          name: s.name as string,
        }));
    const all = extraOptions ? [...base, ...extraOptions] : base;
    return all
      .filter((o) => o.id != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [validSites, fetched, extraOptions]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === selected) ?? null,
    [options, selected],
  );

  const handleChange = useCallback(
    (next: string | null) => {
      if (urlSync) {
        setParams(
          (prev) => {
            const sp = new URLSearchParams(prev);
            if (next) sp.set(paramKey, next);
            else sp.delete(paramKey);
            return sp;
          },
          { replace: true },
        );
      }
      onChange?.(next);
    },
    [urlSync, paramKey, setParams, onChange],
  );

  return (
    <div
      className={cn(
        label ? 'label-input-group flex flex-col gap-1' : '',
        className,
      )}
    >
      {label && <label className="text-sm">{label}</label>}
      <Combobox<SiteSelectOption>
        items={options}
        value={selectedOption}
        onValueChange={(opt) => handleChange(opt?.id ?? null)}
        itemToStringValue={(o) => o.id}
        itemToStringLabel={(o) => o.name}
        disabled={disabled || isLoading}
      >
        <ComboboxInput
          className={cn('h-8 text-sm shadow-none', triggerClassName)}
          placeholder={isLoading ? 'Loading sites…' : placeholder}
          showClear={allowClear}
        />
        <ComboboxContent>
          <ComboboxEmpty>No sites found</ComboboxEmpty>
          <ComboboxList>
            {(o: SiteSelectOption) => (
              <ComboboxItem key={o.id} value={o}>
                {o.name}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
